import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, MapPin, Phone, Clock, Loader2, Search, User, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Marketing() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlineGuests, setOnlineGuests] = useState({});
  const [convertDialog, setConvertDialog] = useState(null);
  const [convertData, setConvertData] = useState({});
  const [converting, setConverting] = useState(false);
  const [sessionPopup, setSessionPopup] = useState(null);

  const fetchGuests = async () => {
    try {
      const res = await axios.get(`${API}/guests`);
      setGuests(res.data || []);
    } catch { /* */ }
    finally { setLoading(false); }
  };

  const fetchOnlineStatus = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await axios.get(`${API}/customer/activity-log?date=${today}`);
      const map = {};
      const now = Date.now();
      for (const c of (res.data || [])) {
        if (c.login_count > 0 && c.activities?.length > 0) {
          const latest = c.activities[0];
          const ts = latest?.timestamp ? new Date(latest.timestamp).getTime() : 0;
          map[c.customer_name] = {
            isActive: (now - ts) < 5 * 60 * 1000,
            latestAction: latest?.action || '',
            latestDetails: latest?.details || '',
            login_time: c.login_time,
            login_count: c.login_count,
            activities: c.activities
          };
        }
      }
      setOnlineGuests(map);
    } catch { /* */ }
  };

  useEffect(() => {
    fetchGuests();
    fetchOnlineStatus();
    const interval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredGuests = guests.filter(g => {
    const info = g.guest_info || {};
    const text = `${info.name} ${info.contact} ${info.city} ${info.state}`.toLowerCase();
    return text.includes(search.toLowerCase());
  });

  const openConvert = (guest) => {
    const info = guest.guest_info || {};
    setConvertData({
      name: info.name || '',
      billing_name: info.name || '',
      dispatch_name: info.name || '',
      contact: info.contact || '',
      city: info.city || '',
      dispatch_city: info.city || '',
      state: info.state || '',
      gstin: '', pincode: '', billing_address: '', company: '',
      transporter_name: '', distance: '', brokers: '', due_days: '',
      contact2: '',
      username: guest.username,
      password: guest.password_display || ''
    });
    setConvertDialog(guest);
  };

  const handleConvert = async () => {
    if (!convertData.name || !convertData.contact || !convertData.city) {
      toast.error("Name, contact, and city are required");
      return;
    }
    setConverting(true);
    try {
      await axios.put(`${API}/guests/${convertDialog.id}/convert-to-customer`, convertData);
      toast.success(`${convertData.name} converted to customer!`);
      setConvertDialog(null);
      fetchGuests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  const toIST = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const ist = new Date(d.getTime() + 5.5*60*60*1000);
    return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
  };

  const actionLabels = {
    login: 'Login', view_orders: 'Viewed Orders', view_ledger: 'Viewed Ledger',
    view_new_order: 'New Order', search_grade: 'Searched Grade', submit_request: 'Submitted Request',
    edit_rate: 'Edited Rate', edit_qty: 'Edited Qty', view_rate: 'Viewed Rate',
    click_freq_grade: 'Freq Grade', click_high_stock_grade: 'High Stock', click_discounted_grade: 'Discounted',
    view_profile: 'Viewed Profile', click_refresh: 'Refreshed', view_dispatch: 'Viewed Dispatch'
  };

  return (
    <div className="p-6" data-testid="marketing-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Marketing</h1>
        <Badge className="text-xs">{guests.length} Leads</Badge>
      </div>

      {/* Leads Tab */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-bold">Leads</h2>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 h-9 text-xs" data-testid="leads-search" />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : filteredGuests.length > 0 ? (
            <div className="space-y-2">
              {filteredGuests.map(guest => {
                const info = guest.guest_info || {};
                const online = onlineGuests[info.name];
                const isLive = online?.isActive;
                const regDate = info.registered_at ? new Date(info.registered_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

                return (
                  <div key={guest.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${isLive ? 'bg-green-50/40 border-green-200' : 'bg-white border-gray-200'}`}
                    data-testid={`lead-${guest.id}`}>
                    {/* Online indicator */}
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${isLive ? 'bg-green-500 animate-pulse' : online ? 'bg-gray-300' : 'bg-gray-200'}`} />
                    
                    {/* Name + Contact */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold truncate">{info.name}</span>
                        {isLive && (
                          <span className="text-[8px] text-green-700 font-medium animate-pulse">
                            {actionLabels[online.latestAction] || 'Online'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Phone className="w-2.5 h-2.5" /> {info.contact}
                        <MapPin className="w-2.5 h-2.5 ml-1" /> {info.city}, {info.state}
                      </div>
                    </div>

                    {/* Source */}
                    {info.source && <Badge variant="outline" className="text-[8px] px-1.5 py-0 shrink-0">{info.source}</Badge>}
                    
                    {/* Registration date */}
                    <span className="text-[9px] text-muted-foreground shrink-0">{regDate}</span>

                    {/* Login sessions button */}
                    {online && (
                      <button onClick={() => setSessionPopup({ name: info.name, ...online })}
                        className="shrink-0 px-1.5 py-1 rounded hover:bg-gray-100 transition-colors" title="View Sessions">
                        <Badge className={`text-[8px] px-1.5 py-0 ${isLive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {toIST(online.login_time)}
                        </Badge>
                      </button>
                    )}

                    {/* Create Customer button */}
                    <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => openConvert(guest)} data-testid={`convert-${guest.id}`}>
                      <UserPlus className="w-3 h-3" /> Create Customer
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No guest leads yet</p>
              <p className="text-xs mt-1">Guests will appear here when they register</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Convert to Customer Dialog */}
      <Dialog open={!!convertDialog} onOpenChange={open => { if (!open) setConvertDialog(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Convert to Customer — {convertDialog?.guest_info?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Party Name *</Label>
                <Input value={convertData.name} onChange={e => setConvertData(p => ({...p, name: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Billing Name</Label>
                <Input value={convertData.billing_name} onChange={e => setConvertData(p => ({...p, billing_name: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Dispatch Name</Label>
                <Input value={convertData.dispatch_name} onChange={e => setConvertData(p => ({...p, dispatch_name: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Company</Label>
                <Input value={convertData.company} onChange={e => setConvertData(p => ({...p, company: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Contact *</Label>
                <Input value={convertData.contact} onChange={e => setConvertData(p => ({...p, contact: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Contact 2</Label>
                <Input value={convertData.contact2} onChange={e => setConvertData(p => ({...p, contact2: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[10px]">City *</Label>
                <Input value={convertData.city} onChange={e => setConvertData(p => ({...p, city: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">State *</Label>
                <Input value={convertData.state} onChange={e => setConvertData(p => ({...p, state: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Pincode</Label>
                <Input value={convertData.pincode} onChange={e => setConvertData(p => ({...p, pincode: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div><Label className="text-[10px]">GSTIN</Label>
              <Input value={convertData.gstin} onChange={e => setConvertData(p => ({...p, gstin: e.target.value}))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Billing Address</Label>
              <Input value={convertData.billing_address} onChange={e => setConvertData(p => ({...p, billing_address: e.target.value}))} className="h-8 text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Dispatch City</Label>
                <Input value={convertData.dispatch_city} onChange={e => setConvertData(p => ({...p, dispatch_city: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Transporter</Label>
                <Input value={convertData.transporter_name} onChange={e => setConvertData(p => ({...p, transporter_name: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[10px]">Distance</Label>
                <Input value={convertData.distance} onChange={e => setConvertData(p => ({...p, distance: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Brokers</Label>
                <Input value={convertData.brokers} onChange={e => setConvertData(p => ({...p, brokers: e.target.value}))} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Due Days</Label>
                <Input value={convertData.due_days} onChange={e => setConvertData(p => ({...p, due_days: e.target.value}))} className="h-8 text-xs" /></div>
            </div>
            <div className="border-t pt-3 mt-3">
              <p className="text-[10px] font-bold text-muted-foreground mb-2">Login Credentials</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Username</Label>
                  <Input value={convertData.username} onChange={e => setConvertData(p => ({...p, username: e.target.value}))} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Password</Label>
                  <Input value={convertData.password} onChange={e => setConvertData(p => ({...p, password: e.target.value}))} className="h-8 text-xs" /></div>
              </div>
            </div>
            <Button onClick={handleConvert} disabled={converting} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Convert to Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Sessions Popup */}
      <Dialog open={!!sessionPopup} onOpenChange={open => { if (!open) setSessionPopup(null); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-green-600" /> Sessions — {sessionPopup?.name}
            </DialogTitle>
          </DialogHeader>
          {sessionPopup && (() => {
            const acts = [...(sessionPopup.activities || [])].reverse();
            const sessions = [];
            let cur = null;
            for (const a of acts) {
              if (a.action === 'login') { if (cur) sessions.push(cur); cur = { login_time: a.timestamp, actions: [] }; }
              if (cur) cur.actions.push(a);
            }
            if (cur) sessions.push(cur);
            sessions.reverse();
            return (
              <div className="space-y-3 mt-2">
                {sessions.map((s, si) => (
                  <details key={si} open={si === 0} className="border rounded-lg overflow-hidden group">
                    <summary className="bg-green-50 px-3 py-2 border-b flex items-center gap-2 cursor-pointer list-none select-none hover:bg-green-100">
                      <ChevronDown className="w-3 h-3 text-green-600 transition-transform group-open:rotate-180" />
                      <span className="text-xs font-bold text-green-800">
                        {si === 0 ? 'Latest' : `Session ${sessions.length - si}`} — {toIST(s.login_time)}
                      </span>
                      <Badge variant="outline" className="text-[9px] ml-auto">{s.actions.length}</Badge>
                    </summary>
                    <div className="divide-y">
                      {s.actions.map((a, ai) => (
                        <div key={ai} className={`px-3 py-1.5 flex items-start gap-2 text-[11px] ${a.action === 'view_rate' ? 'bg-blue-50/50' : ''}`}>
                          <span className="font-mono text-muted-foreground w-12 shrink-0">{toIST(a.timestamp)}</span>
                          <span className={`font-semibold ${a.action === 'view_rate' ? 'text-blue-700' : 'text-gray-700'}`}>{actionLabels[a.action] || a.action}</span>
                          {a.details && <span className="text-gray-500 truncate">{a.details}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
                {sessions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No sessions</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
